import { useEffect, useState } from "react";
import { Phone } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const PROJECT_TYPES = ["Prototype", "Production machining", "Sub-assembly or full assembly", "Other manufacturing inquiry"];
const CAPABILITIES = ["5-axis machining", "CNC milling and turning", "Wire or sinker EDM", "Grinding", "Assembly", "Not sure yet"];
const CAP_MAP = {
  "5-axis machining": "5-axis machining",
  "cnc milling & turning": "CNC milling and turning",
  "wire & sinker edm": "Wire or sinker EDM",
  "grinding": "Grinding",
  "sub-assembly & full assembly": "Assembly",
};

const EMPTY = {
  name: "", company: "", email: "", phone: "",
  projectType: "", capability: "", quantity: "", deliveryDate: "", details: "",
};

const goTo = (id) => (e) => {
  e.preventDefault();
  const el = document.querySelector(id);
  if (window.__lenis && el) window.__lenis.scrollTo(el, { offset: -90 });
  else el?.scrollIntoView({ behavior: "smooth" });
};

// N — Project inquiry form. Submissions are stored through the site's own backend.
export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    const onPrefill = (e) => {
      const d = e.detail || {};
      setForm((f) => {
        const next = { ...f };
        if (d.capability) {
          const key = d.capability.toLowerCase();
          next.capability = CAP_MAP[key] || (CAPABILITIES.some((c) => c.toLowerCase() === key) ? d.capability : "Not sure yet");
        }
        if (d.application) {
          next.details = next.details ? `Application: ${d.application}\n${next.details}` : `Application: ${d.application}\n`;
          if (!next.projectType) next.projectType = "Other manufacturing inquiry";
        }
        return next;
      });
    };
    window.addEventListener("pmp:prefill", onPrefill);
    return () => window.removeEventListener("pmp:prefill", onPrefill);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="on-light bg-[var(--cream-2)] py-24 sm:py-32">
      <div className="wrap grid gap-14 lg:grid-cols-[1fr_1.15fr] lg:gap-20">
        <div>
          <Reveal>
            <p className="eyebrow">
              <span className="no">11</span>
              <span className="rule" />
              PROJECT INQUIRY
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="headline mt-5 text-4xl text-[var(--ink)] sm:text-5xl lg:text-[3.4rem]">
              Let’s start with what <span className="font-serif-i">has to be right.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-5 max-w-md text-base leading-relaxed text-[var(--grey)] sm:text-lg [text-wrap:pretty]">
              Share the essentials of your project so we can begin a useful conversation.
            </p>
          </Reveal>
          <Reveal delay={0.22}>
            <div className="mt-8 flex items-center gap-3 rounded-xl border border-[var(--line)] bg-white px-5 py-4">
              <Phone className="h-4 w-4 shrink-0 text-[var(--blue)]" />
              <p className="text-[14.5px] text-[var(--ink-soft)]">
                Prefer to speak with us? Call{" "}
                <a href="tel:+19704827676" data-testid="contact-phone-link" className="font-medium text-[var(--blue)] hover:underline">
                  (970) 482-7676
                </a>
                .
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          {status === "success" ? (
            <div data-testid="contact-success" className="flex h-full min-h-[24rem] flex-col items-start justify-center rounded-[0.75rem] border border-[var(--blue)]/30 bg-white p-10">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--blue)] font-mono text-lg text-white">✓</span>
              <p className="mt-6 text-xl font-semibold tracking-[-0.02em] text-[var(--ink)]">
                Thank you for contacting PMP. Your inquiry has been received.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="rounded-[0.75rem] border border-[var(--line)] bg-white p-7 sm:p-9">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="field-label" htmlFor="f-name">Name*</label>
                  <input id="f-name" required data-testid="contact-name" className="field" value={form.name} onChange={set("name")} autoComplete="name" />
                </div>
                <div>
                  <label className="field-label" htmlFor="f-company">Company*</label>
                  <input id="f-company" required data-testid="contact-company" className="field" value={form.company} onChange={set("company")} autoComplete="organization" />
                </div>
                <div>
                  <label className="field-label" htmlFor="f-email">Work email*</label>
                  <input id="f-email" required type="email" data-testid="contact-email" className="field" value={form.email} onChange={set("email")} autoComplete="email" />
                </div>
                <div>
                  <label className="field-label" htmlFor="f-phone">Phone</label>
                  <input id="f-phone" type="tel" data-testid="contact-phone" className="field" value={form.phone} onChange={set("phone")} autoComplete="tel" />
                </div>
                <div>
                  <label className="field-label" htmlFor="f-type">Project type</label>
                  <select id="f-type" data-testid="contact-project-type" className="field" value={form.projectType} onChange={set("projectType")}>
                    <option value="">Select…</option>
                    {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label" htmlFor="f-cap">Capability of interest</label>
                  <select id="f-cap" data-testid="contact-capability" className="field" value={form.capability} onChange={set("capability")}>
                    <option value="">Select…</option>
                    {CAPABILITIES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label" htmlFor="f-qty">Estimated quantity</label>
                  <input id="f-qty" data-testid="contact-quantity" className="field" value={form.quantity} onChange={set("quantity")} placeholder="e.g. 25" />
                </div>
                <div>
                  <label className="field-label" htmlFor="f-date">Requested delivery date</label>
                  <input id="f-date" type="date" data-testid="contact-delivery" className="field" value={form.deliveryDate} onChange={set("deliveryDate")} />
                </div>
              </div>
              <div className="mt-5">
                <label className="field-label" htmlFor="f-details">Project details*</label>
                <textarea
                  id="f-details"
                  required
                  rows={5}
                  data-testid="contact-details"
                  className="field resize-y"
                  placeholder="What do you need made? Include material, quantity, critical features, and any inspection or documentation requirements."
                  value={form.details}
                  onChange={set("details")}
                />
              </div>
              {status === "error" && (
                <p data-testid="contact-error" className="mt-4 rounded-lg bg-[#fca5a5]/25 px-4 py-3 text-[13.5px] text-[#b91c1c]">
                  Your inquiry could not be submitted. Please try again or call (970) 482-7676.
                </p>
              )}
              <button type="submit" data-testid="contact-submit" disabled={status === "sending"} className="btn btn-blue mt-6 w-full justify-center disabled:opacity-60 sm:w-auto">
                {status === "sending" ? "Sending…" : "Discuss My Project"}
              </button>
              <p className="mt-4 text-[12.5px] leading-relaxed text-[var(--grey)]">
                Your inquiry goes directly to PMP through this website. <a href="tel:+19704827676" className="text-[var(--blue)] hover:underline">Call (970) 482-7676</a> any time.
              </p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
